import React, { useState, useCallback, useRef, useEffect } from 'react';
import './TripleGame.css';

/* ═══════════════════════════════════════════════════════════════
   CONSTANTES
═══════════════════════════════════════════════════════════════ */
const TRIPLES = [20, 19, 18, 17, 16, 15];
const LIMITS = [5, 10];

const SCORE_LABELS = ['Ninguno', '1 Triple', '2 Triples', '¡Perfecto!'];

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════════ */
const TripleGame = ({ onBack }) => {
  /* ── Configuración setup ── */
  const [name1, setName1]         = useState('');
  const [name2, setName2]         = useState('');
  const [limit, setLimit]         = useState(5);
  const [setupTriple, setSetupTriple] = useState(20);

  /* ── Fase del juego ── */
  const [phase, setPhase]         = useState('setup'); // 'setup' | 'playing' | 'segment-end' | 'finished'

  /* ── Estado de partida ── */
  const [currentTriple, setCurrentTriple]   = useState(20);
  const [currentPlayer, setCurrentPlayer]   = useState(1); // 1 | 2
  const [isBonusTurn, setIsBonusTurn]       = useState(false);
  const [p1Score, setP1Score]               = useState(0);
  const [p2Score, setP2Score]               = useState(0);
  const [p1Wins, setP1Wins]                 = useState(0);
  const [p2Wins, setP2Wins]                 = useState(0);
  const [segmentHistory, setSegmentHistory] = useState([]);
  const [segmentWinner, setSegmentWinner]   = useState(null); // 1 | 2 | 'draw'

  /* ── Animaciones ── */
  const [scoreFlash, setScoreFlash]     = useState(null); // { player: 1|2, value: n }
  const [cardPulse, setCardPulse]       = useState(false);
  const [bonusAnim, setBonusAnim]       = useState(false);

  /* ── Selector en segment-end ── */
  const [nextTripleSelection, setNextTripleSelection] = useState(null);

  /* ── Modal de estadísticas ── */
  const [showStatsModal, setShowStatsModal] = useState(false);

  const flashTimerRef = useRef(null);

  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  const resolvedP1Name = name1.trim() || 'Jugador 1';
  const resolvedP2Name = name2.trim() || 'Jugador 2';

  /* ═══════════════════════════════════════════
     HANDLERS
  ═══════════════════════════════════════════ */

  const handleStart = useCallback(() => {
    setCurrentTriple(setupTriple);
    setCurrentPlayer(1);
    setIsBonusTurn(false);
    setP1Score(0);
    setP2Score(0);
    setP1Wins(0);
    setP2Wins(0);
    setSegmentHistory([]);
    setSegmentWinner(null);
    setNextTripleSelection(null);
    setCardPulse(false);
    setBonusAnim(false);
    setPhase('playing');
  }, [setupTriple]);

  const handleScore = useCallback((triples) => {
    const newP1Score = currentPlayer === 1 ? p1Score + triples : p1Score;
    const newP2Score = currentPlayer === 2 ? p2Score + triples : p2Score;

    if (currentPlayer === 1) setP1Score(newP1Score);
    else setP2Score(newP2Score);

    /* Flash numérico en marcador */
    if (triples > 0) {
      setScoreFlash({ player: currentPlayer, value: triples });
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setScoreFlash(null), 600);
    }

    /* Turno de bonus: 3 triples → mismo jugador repite */
    if (triples === 3) {
      setBonusAnim(true);
      setTimeout(() => setBonusAnim(false), 900);
      setIsBonusTurn(true);
      return;
    }

    setIsBonusTurn(false);

    /* Comprobar fin de segmento */
    if (newP1Score >= limit || newP2Score >= limit) {
      let winner;
      if (newP1Score > newP2Score)      winner = 1;
      else if (newP2Score > newP1Score) winner = 2;
      else                              winner = 'draw';

      setSegmentWinner(winner);
      setSegmentHistory(prev => [
        ...prev,
        { triple: currentTriple, p1Score: newP1Score, p2Score: newP2Score, winner },
      ]);

      if (winner === 1) setP1Wins(w => w + 1);
      else if (winner === 2) setP2Wins(w => w + 1);

      setNextTripleSelection(null);
      setTimeout(() => setPhase('segment-end'), 200);
      return;
    }

    /* Cambiar jugador con animación de card */
    setCardPulse(true);
    setTimeout(() => {
      setCurrentPlayer(prev => (prev === 1 ? 2 : 1));
      setCardPulse(false);
    }, 160);
  }, [currentPlayer, p1Score, p2Score, limit, currentTriple]);

  const handleNextTriple = useCallback((triple) => {
    setCurrentTriple(triple);
    setCurrentPlayer(1);
    setIsBonusTurn(false);
    setP1Score(0);
    setP2Score(0);
    setSegmentWinner(null);
    setNextTripleSelection(null);
    setPhase('playing');
  }, []);

  const handleExit = useCallback(() => {
    setShowStatsModal(true);
    setPhase('finished');
  }, []);

  const handleRestart = useCallback(() => {
    setP1Score(0);
    setP2Score(0);
    setP1Wins(0);
    setP2Wins(0);
    setSegmentHistory([]);
    setSegmentWinner(null);
    setNextTripleSelection(null);
    setShowStatsModal(false);
    setPhase('setup');
  }, []);

  /* ═══════════════════════════════════════════
     RENDER — SETUP
  ═══════════════════════════════════════════ */
  if (phase === 'setup') {
    const canStart = true; // nombres opcionales

    return (
      <div className="tg-container">
        <div className="tg-setup">
          <button className="tg-back-btn" onClick={onBack}>← Volver</button>

          <div className="tg-setup-logo">
            <img src="/Logo26.png" alt="501 Dart League" className="tg-app-logo" />
          </div>
          <h1 className="tg-setup-title">Duelo de Triples</h1>
          <p className="tg-setup-subtitle">Elige límite, triple y compite</p>

          {/* Límite */}
          <p className="tg-section-label">¿A cuántos triples?</p>
          <div className="tg-segment-row">
            {LIMITS.map(l => (
              <button
                key={l}
                className={`tg-segment-btn${limit === l ? ' active' : ''}`}
                onClick={() => setLimit(l)}
              >
                A {l} triples
              </button>
            ))}
          </div>

          {/* Triple */}
          <p className="tg-section-label">Triple a jugar</p>
          <div className="tg-triples-grid">
            {TRIPLES.map(t => (
              <button
                key={t}
                className={`tg-triple-select-btn${setupTriple === t ? ' active' : ''}`}
                onClick={() => setSetupTriple(t)}
              >
                <span className="tg-triple-select-num">{t}</span>
                <span className="tg-triple-select-sub">Triple</span>
              </button>
            ))}
          </div>

          {/* Nombres */}
          <p className="tg-section-label">Jugadores</p>
          <div className="tg-setup-card">
            <label className="tg-input-label">Jugador 1</label>
            <input
              className="tg-input"
              type="text"
              value={name1}
              onChange={e => setName1(e.target.value)}
              placeholder="Jugador 1"
              maxLength={20}
              autoComplete="off"
            />
            <label className="tg-input-label" style={{ marginTop: 14 }}>Jugador 2</label>
            <input
              className="tg-input"
              type="text"
              value={name2}
              onChange={e => setName2(e.target.value)}
              placeholder="Jugador 2"
              maxLength={20}
              autoComplete="off"
              onKeyDown={e => e.key === 'Enter' && handleStart()}
            />
          </div>

          {/* Reglas */}
          <ul className="tg-rules">
            <li>🎯 Cada turno registra cuántos triples metiste (0–3)</li>
            <li>🔄 Si metes los 3, ¡vuelves a tirar de bonus!</li>
            <li>🏆 Primero en llegar a <strong>{limit}</strong> gana la ronda</li>
            <li>➕ Cuando termina, elige otro triple o sal</li>
          </ul>

          <button className="tg-start-btn" onClick={handleStart} disabled={!canStart}>
            🎯 Iniciar Partida
          </button>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER — PLAYING
  ═══════════════════════════════════════════ */
  if (phase === 'playing') {
    const isP1Turn  = currentPlayer === 1;
    const p1Pct     = Math.min(Math.round((p1Score / limit) * 100), 100);
    const p2Pct     = Math.min(Math.round((p2Score / limit) * 100), 100);
    const activeName = isP1Turn ? resolvedP1Name : resolvedP2Name;

    return (
      <div className="tg-container">
        <div className="tg-playing">

          {/* ── Header ── */}
          <div className="tg-playing-header">
            <button className="tg-back-sm" onClick={onBack}>← Volver</button>
            <div className="tg-header-center">
              <div className="tg-header-triple">Triple {currentTriple}</div>
              <div className="tg-header-meta">Meta: {limit} triples</div>
            </div>
            <div className="tg-header-round">
              {segmentHistory.length + 1}ª ronda
            </div>
          </div>

          {/* ── Marcador ── */}
          <div className="tg-scoreboard">
            {/* Jugador 1 */}
            <div className={`tg-player-panel${isP1Turn ? ' active' : ''}${scoreFlash?.player === 1 ? ' flashing' : ''}`}>
              {isP1Turn && <div className="tg-active-dot" />}
              <div className="tg-panel-wins">
                {Array.from({ length: p1Wins }).map((_, i) => (
                  <span key={i} className="tg-win-star">★</span>
                ))}
                {p1Wins === 0 && <span className="tg-win-star empty">☆</span>}
              </div>
              <div className="tg-panel-name">{resolvedP1Name}</div>
              <div className="tg-panel-score">
                {p1Score}
                {scoreFlash?.player === 1 && (
                  <span className="tg-score-pop">+{scoreFlash.value}</span>
                )}
              </div>
              <div className="tg-progress-track">
                <div className="tg-progress-fill" style={{ width: `${p1Pct}%` }} />
              </div>
              <div className="tg-progress-label">{p1Score} / {limit}</div>
            </div>

            {/* VS */}
            <div className="tg-vs-divider">
              <span className="tg-vs-text">VS</span>
            </div>

            {/* Jugador 2 */}
            <div className={`tg-player-panel${!isP1Turn ? ' active' : ''}${scoreFlash?.player === 2 ? ' flashing' : ''}`}>
              {!isP1Turn && <div className="tg-active-dot" />}
              <div className="tg-panel-wins">
                {Array.from({ length: p2Wins }).map((_, i) => (
                  <span key={i} className="tg-win-star">★</span>
                ))}
                {p2Wins === 0 && <span className="tg-win-star empty">☆</span>}
              </div>
              <div className="tg-panel-name">{resolvedP2Name}</div>
              <div className="tg-panel-score">
                {p2Score}
                {scoreFlash?.player === 2 && (
                  <span className="tg-score-pop">+{scoreFlash.value}</span>
                )}
              </div>
              <div className="tg-progress-track">
                <div className="tg-progress-fill" style={{ width: `${p2Pct}%` }} />
              </div>
              <div className="tg-progress-label">{p2Score} / {limit}</div>
            </div>
          </div>

          {/* ── Card central ── */}
          <div className={`tg-game-card${isP1Turn ? ' p1' : ' p2'}${cardPulse ? ' pulse-out' : ''}`}>
            <img src="/Logo26.png" alt="" className="tg-game-card-logo" aria-hidden="true" />
            {isBonusTurn && (
              <div className={`tg-bonus-badge${bonusAnim ? ' pop' : ''}`}>
                🔥 TURNO BONUS
              </div>
            )}
            <div className="tg-card-turn-label">Turno de</div>
            <div className="tg-card-active-name">{activeName}</div>
            <div className="tg-triple-ring">
              <div className="tg-triple-ring-inner">
                <span className="tg-triple-ring-number">{currentTriple}</span>
              </div>
            </div>
            <div className="tg-card-question">¿Cuántos triples metiste?</div>
          </div>

          {/* ── Botones de puntuación ── */}
          <div className="tg-score-buttons">
            {[0, 1, 2, 3].map(n => (
              <button
                key={n}
                className={`tg-score-btn score-${n}`}
                onClick={() => handleScore(n)}
              >
                <span className="tg-score-btn-number">{n}</span>
                <span className="tg-score-btn-label">{SCORE_LABELS[n]}</span>
                {n === 3 && <span className="tg-score-btn-bonus">+ Bonus</span>}
              </button>
            ))}
          </div>

        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER — SEGMENT END
  ═══════════════════════════════════════════ */
  if (phase === 'segment-end') {
    const lastSeg    = segmentHistory[segmentHistory.length - 1];
    const winnerName = segmentWinner === 1 ? resolvedP1Name
                     : segmentWinner === 2 ? resolvedP2Name
                     : null;

    return (
      <div className="tg-container">
        <div className="tg-segment-end">

          {/* Banner resultado */}
          <div className={`tg-result-banner seg-${segmentWinner}`}>
            <div className="tg-result-triple-label">Triple {lastSeg?.triple ?? currentTriple}</div>
            <div className="tg-result-headline">
              {winnerName ? `🏆 ¡Gana ${winnerName}!` : '🤝 ¡Empate!'}
            </div>
            <div className="tg-result-scores">
              {lastSeg?.p1Score ?? 0} – {lastSeg?.p2Score ?? 0}
            </div>
          </div>

          {/* Marcador general */}
          <div className="tg-overall-card">
            <div className="tg-overall-title">Marcador general</div>
            <div className="tg-overall-row">
              <div className={`tg-overall-player${p1Wins > p2Wins ? ' leading' : ''}`}>
                <div className="tg-overall-name">{resolvedP1Name}</div>
                <div className="tg-overall-wins">{p1Wins}</div>
                <div className="tg-overall-wins-label">rondas</div>
              </div>
              <div className="tg-overall-sep">–</div>
              <div className={`tg-overall-player${p2Wins > p1Wins ? ' leading' : ''}`}>
                <div className="tg-overall-name">{resolvedP2Name}</div>
                <div className="tg-overall-wins">{p2Wins}</div>
                <div className="tg-overall-wins-label">rondas</div>
              </div>
            </div>
          </div>

          {/* Historial de rondas */}
          {segmentHistory.length > 1 && (
            <div className="tg-history-card">
              <div className="tg-history-title">Historial</div>
              {segmentHistory.map((seg, i) => (
                <div key={i} className="tg-history-row">
                  <span className="tg-history-triple">T{seg.triple}</span>
                  <span className="tg-history-scores">{seg.p1Score} – {seg.p2Score}</span>
                  <span className={`tg-history-winner w${seg.winner}`}>
                    {seg.winner === 1 ? resolvedP1Name
                   : seg.winner === 2 ? resolvedP2Name
                   : '–'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Selector siguiente triple */}
          <div className="tg-next-triple-section">
            <p className="tg-section-label">Siguiente triple</p>
            <div className="tg-triples-grid">
              {TRIPLES.map(t => (
                <button
                  key={t}
                  className={`tg-triple-select-btn${nextTripleSelection === t ? ' active' : ''}`}
                  onClick={() => setNextTripleSelection(t)}
                >
                  <span className="tg-triple-select-num">{t}</span>
                  <span className="tg-triple-select-sub">Triple</span>
                </button>
              ))}
            </div>
          </div>

          {/* Acciones */}
          <div className="tg-segment-actions">
            <button
              className="tg-play-next-btn"
              disabled={!nextTripleSelection}
              onClick={() => nextTripleSelection && handleNextTriple(nextTripleSelection)}
            >
              🎯 Jugar Triple {nextTripleSelection ?? '···'}
            </button>
            <button className="tg-finish-btn" onClick={handleExit}>
              Ver resultado final
            </button>
          </div>

        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER — FINISHED
  ═══════════════════════════════════════════ */
  if (phase === 'finished') {
    const overallWinner = p1Wins > p2Wins ? resolvedP1Name
                        : p2Wins > p1Wins ? resolvedP2Name
                        : null;

    /* ── Cálculo de estadísticas de acierto ── */
    const maxPossible    = segmentHistory.length * limit;
    const totalTrpP1     = segmentHistory.reduce((s, h) => s + Math.min(h.p1Score, limit), 0);
    const totalTrpP2     = segmentHistory.reduce((s, h) => s + Math.min(h.p2Score, limit), 0);
    const accP1          = maxPossible > 0 ? Math.round((totalTrpP1 / maxPossible) * 100) : 0;
    const accP2          = maxPossible > 0 ? Math.round((totalTrpP2 / maxPossible) * 100) : 0;

    return (
      <div className="tg-container">
        <div className="tg-finished">

          <div className="tg-finished-icon">🎯</div>
          <h2 className="tg-finished-title">Partida Terminada</h2>

          {overallWinner ? (
            <p className="tg-finished-winner">🏆 ¡Gana {overallWinner}!</p>
          ) : (
            <p className="tg-finished-winner">🤝 ¡Empate!</p>
          )}

          {/* Stats finales */}
          <div className="tg-finished-stats">
            {[
              { name: resolvedP1Name, wins: p1Wins, triples: totalTrpP1, acc: accP1 },
              { name: resolvedP2Name, wins: p2Wins, triples: totalTrpP2, acc: accP2 },
            ].map(({ name, wins, triples, acc }, i) => {
              const isChamp = (i === 0 && p1Wins > p2Wins) || (i === 1 && p2Wins > p1Wins);
              return (
                <div key={i} className={`tg-finished-player${isChamp ? ' champion' : ''}`}>
                  <div className="tg-finished-player-name">
                    {isChamp && <span className="tg-crown">👑 </span>}
                    {name}
                  </div>
                  <div className="tg-finished-player-data">
                    <div className="tg-finished-stat">
                      <span className="tg-finished-stat-val">{wins}</span>
                      <span className="tg-finished-stat-label">rondas</span>
                    </div>
                    <div className="tg-finished-stat">
                      <span className="tg-finished-stat-val">{triples}</span>
                      <span className="tg-finished-stat-label">triples</span>
                    </div>
                    <div className="tg-finished-stat">
                      <span className="tg-finished-stat-val tg-acc">{acc}%</span>
                      <span className="tg-finished-stat-label">acierto</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Historial completo */}
          {segmentHistory.length > 0 && (
            <div className="tg-history-card tg-history-final">
              <div className="tg-history-title">Historial de rondas</div>
              {segmentHistory.map((seg, i) => (
                <div key={i} className="tg-history-row">
                  <span className="tg-history-triple">T{seg.triple}</span>
                  <span className="tg-history-scores">{seg.p1Score} – {seg.p2Score}</span>
                  <span className={`tg-history-winner w${seg.winner}`}>
                    {seg.winner === 1 ? resolvedP1Name
                   : seg.winner === 2 ? resolvedP2Name
                   : 'Empate'}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="tg-finished-actions">
            <button className="tg-play-next-btn" onClick={handleRestart}>
              🔄 Nueva Partida
            </button>
            <button className="tg-finish-btn" onClick={onBack}>
              Salir
            </button>
          </div>

        </div>

        {/* ══ MODAL DE ESTADÍSTICAS ══ */}
        {showStatsModal && (
          <div className="tg-stats-overlay" onClick={() => setShowStatsModal(false)}>
            <div className="tg-stats-modal" onClick={e => e.stopPropagation()}>

              <div className="tg-stats-modal-header">
                <span className="tg-stats-modal-icon">📊</span>
                <h3 className="tg-stats-modal-title">Estadísticas de la partida</h3>
              </div>

              <div className="tg-stats-global">
                <div className="tg-stats-global-label">Total posible: {maxPossible} triples</div>
                <div className="tg-stats-global-sub">({segmentHistory.length} ronda{segmentHistory.length !== 1 ? 's' : ''} × meta {limit})</div>
              </div>

              {/* Barras de acierto por jugador */}
              {[
                { name: resolvedP1Name, scored: totalTrpP1, acc: accP1, isP1: true },
                { name: resolvedP2Name, scored: totalTrpP2, acc: accP2, isP1: false },
              ].map(({ name, scored, acc, isP1 }) => (
                <div key={name} className={`tg-stats-player-block${isP1 ? ' p1' : ' p2'}`}>
                  <div className="tg-stats-player-header">
                    <span className="tg-stats-player-name">{name}</span>
                    <span className="tg-stats-pct">{acc}%</span>
                  </div>
                  <div className="tg-stats-bar-track">
                    <div
                      className={`tg-stats-bar-fill${isP1 ? ' p1' : ' p2'}`}
                      style={{ width: `${acc}%` }}
                    />
                  </div>
                  <div className="tg-stats-detail">
                    {scored} de {maxPossible} triples
                  </div>
                </div>
              ))}

              {/* Desglose por triple */}
              {segmentHistory.length > 0 && (
                <div className="tg-stats-breakdown">
                  <div className="tg-stats-breakdown-title">Desglose por ronda</div>
                  {segmentHistory.map((seg, i) => {
                    const p1Pct = Math.round((Math.min(seg.p1Score, limit) / limit) * 100);
                    const p2Pct = Math.round((Math.min(seg.p2Score, limit) / limit) * 100);
                    return (
                      <div key={i} className="tg-stats-seg-row">
                        <span className="tg-stats-seg-triple">T{seg.triple}</span>
                        <div className="tg-stats-seg-players">
                          <span className="tg-stats-seg-val p1">{seg.p1Score}/{limit} ({p1Pct}%)</span>
                          <span className="tg-stats-seg-sep">·</span>
                          <span className="tg-stats-seg-val p2">{seg.p2Score}/{limit} ({p2Pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button className="tg-stats-close-btn" onClick={() => setShowStatsModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        )}

      </div>
    );
  }

  return null;
};

export default TripleGame;
